import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Package, Shield, Truck } from "lucide-react";

interface Box {
  tracking_number?: string;
  shipping_label_url?: string;
  insurance_opted_out?: boolean;
  easypost_tracking_url?: string;
}

interface ShippingToStorageCardProps {
  box: Box;
  onEmailLabel: () => void;
}

export function ShippingToStorageCard({ box, onEmailLabel }: ShippingToStorageCardProps) {
  if (!box.tracking_number) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Truck className="h-5 w-5" />
          Shipping to Storage
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Tracking Number</p>
            <Badge variant="secondary">In Transit</Badge>
          </div>
          <div className="flex items-center gap-2">
            <code className="text-sm bg-muted px-2 py-1 rounded flex-1">
              {box.tracking_number}
            </code>
            <Button
              size="sm"
              variant="outline"
              asChild
            >
              <a
                href={box.easypost_tracking_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Track
              </a>
            </Button>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Package className="h-4 w-4 mt-0.5 text-muted-foreground" />
          <div className="flex-1">
            <p className="text-sm font-medium">Shipping Label</p>
            <div className="flex gap-2 mt-2">
              {box.shipping_label_url && (
                <Button size="sm" variant="outline" asChild>
                  <a href={box.shipping_label_url} download target="_blank" rel="noopener noreferrer">
                    Download
                  </a>
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={onEmailLabel}>
                Email Label
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Shield className="h-4 w-4 mt-0.5 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">Insurance</p>
            <p className="text-sm text-muted-foreground">
              {box.insurance_opted_out ? "Not included" : "Included ($100 coverage)"}
            </p>
          </div>
        </div>

        <div className="pt-3 border-t">
          <p className="text-xs text-muted-foreground">
            Your box is on its way to our secure storage facility. You'll receive updates via email.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
